/**
 * Role availability across divisions.
 *
 * Registration roles are configured PER DIVISION (`is_player_registration` /
 * `is_team_registration`), not per competition — the competition-level twins
 * exist on the payload but have never gated anything. So "does this
 * registration offer both roles?" can only be answered by looking at every
 * division the reader can see.
 */

import type { StartDivision, StartRole } from './types'

/** Whether one division accepts registrations for a role. */
export const divisionOffersRole = (
  division: StartDivision | null | undefined,
  role: StartRole
): boolean =>
  role === 'player'
    ? Boolean(division?.is_player_registration)
    : Boolean(division?.is_team_registration)

/**
 * Why a full division cannot be picked.
 *
 * The card used to carry two badges for this one fact — "Division Full" and
 * "0 Spots Left" — which said the same thing twice and competed with the
 * attributes beside them. The reason is now given once, on hover, the same way
 * a full team card explains the same state (`TEAM_FULL_MESSAGE`).
 */
export const DIVISION_FULL_MESSAGE = 'Registration Is Full'

/**
 * Team registration is closed once the division is out of spots.
 *
 * `max_teams: null` means uncapped — the common case in real payloads — so it
 * must read as "not full" rather than falling into `null - n <= 0`.
 */
export const isDivisionTeamFull = (
  division: StartDivision | null | undefined
): boolean => {
  const maxTeams = division?.max_teams
  if (maxTeams == null) {
    return false
  }
  return maxTeams - (division?.team_count_enrolled ?? 0) <= 0
}

/** Whether a division can be picked at all for a given role. */
export const divisionSelectableForRole = (
  division: StartDivision | null | undefined,
  role: StartRole
): boolean => {
  if (!divisionOffersRole(division, role)) {
    return false
  }
  return role === 'team' ? !isDivisionTeamFull(division) : true
}

/**
 * Narrow a division list to the ones that accept the chosen role.
 *
 * A null role means the reader has not chosen yet, so nothing is filtered —
 * they should see the whole offer before committing to a side.
 */
export const filterDivisionsByRole = <T extends StartDivision>(
  divisions: T[] | null | undefined,
  role: StartRole | null | undefined
): T[] => {
  const all = divisions ?? []
  if (!role) {
    return all
  }
  return all.filter((division) => divisionOffersRole(division, role))
}

/**
 * The single role every visible division offers, or null when they disagree.
 *
 * When a competition only takes players, asking "player or team?" is a question
 * with one answer — this is what lets the start step drop the role zone
 * entirely. It returns null the moment ANY division offers something different,
 * because a filtered-away division is still a division the reader could reach
 * by changing the age/gender filters.
 */
export const soleRoleAcrossDivisions = (
  divisions: StartDivision[] | null | undefined
): StartRole | null => {
  const all = divisions ?? []
  if (all.length === 0) {
    return null
  }
  const anyPlayer = all.some((d) => divisionOffersRole(d, 'player'))
  const anyTeam = all.some((d) => divisionOffersRole(d, 'team'))

  if (anyPlayer && !anyTeam) {
    return 'player'
  }
  if (anyTeam && !anyPlayer) {
    return 'team'
  }
  // Both offered, or neither — the reader has to be asked (or told nothing is
  // available, which the caller handles as its own empty state).
  return null
}
